<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';
import recurringAppointmentMixin from '@/components/mixins/recurringAppointmentMixin';
import carrierFavoritesMixin from '@/components/mixins/carrierFavoritesMixin';

/**
 * @displayName Appointment Details Base
 */
export default {
  mixins: [appointmentMixin, recurringAppointmentMixin, carrierFavoritesMixin],
  props: {
    /**
     * Appointment to display details for
     */
    appointment: {
      type: Object,
      required: true
    },
    /**
     * Display status editing options
     */
    shouldEditStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    shouldDisplayCancelButton: {
      type: Boolean,
      required: false,
      default: true
    },
    appointmentWarehouse: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: false
    }
  },
  computed: {
    canReschedule() {
      return (
        this.$rolePermissions.canUpdateAppointment &&
        this.novaCore.isRescheduleAllowed(this.appointment.status) &&
        !this.readOnly
      );
    },
    reschedulesCount() {
      return this.novaCore.getReschedulesCount(this.appointment);
    },
    reschedulesString() {
      return this.novaCore.getReschedulesString(this.reschedulesCount);
    }
  },
  data() {
    return {
      showRescheduleDialog: false,
      loading: true,
      tags: this.appointment.tags,
      lockTagEditing: false,
      isAuditLogVisible: false,
      appointmentTableRows: {
        dockName: {
          label: 'Dock Name',
          value: `${this.appointment.readableDockName ?? this.appointment.dock?.name}${
            this.appointment.dock.isActive === false ? ' (Inactive)' : ''
          }`,
          tdClass: 'pb-4',
          isHidden: false
        },
        loadType: {
          label: 'Load Type',
          value: '',
          tdClass: 'pb-4',
          isHidden: false
        },
        refNumber: {
          label: 'Reference Number',
          value: this.appointment.refNumber || '----',
          tdClass: 'pb-4',
          isHidden: false
        }
      }
    };
  },
  methods: {
    handleAuditLogExpand() {
      this.isAuditLogVisible = !this.isAuditLogVisible;
      this.trackAuditLogMixpanelEvent();
      if (this.isAuditLogVisible) {
        setTimeout(() => {
          const element = document.getElementById('audit_log_element');
          if (element) {
            element.scrollIntoView();
          }
        }, 500);
      }
    },
    trackAuditLogMixpanelEvent() {
      this.mixpanel.track(this.mixpanel.events.VIEW.AUDIT_LOG, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Audit Log Entity': 'Appointment'
      });
    },
    trackApptDetailsMixpanelEvent() {
      const entryPoint = this.mixpanel.getEntryPoint(this, [
        {
          entryPoint: 'Carrier Insights',
          component: 'carrier-metrics-table'
        },
        {
          entryPoint: 'Mobile List View',
          component: 'appointments-list-mobile'
        },
        {
          entryPoint: 'Appointments List',
          component: 'appointments-list'
        },
        {
          entryPoint: 'Self Check-in List',
          component: 'check-in-notification-dropdown'
        },
        {
          entryPoint: 'Appointments Grid',
          component: 'appointments-page'
        },
        {
          entryPoint: 'Search Appointments',
          component: 'appointments-search-results'
        }
      ]);
      this.mixpanel.track(this.mixpanel.events.MODULE.APPOINTMENT.APPOINTMENT_DETAILS, {
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Warehouse Name': this.appointmentWarehouse.name,
        'Warehouse ID': this.appointmentWarehouse.id,
        'Appointment ID': this.appointment.id,
        'Entry Point': entryPoint
      });
    },
    initializeRowValues() {
      const refNumSettings = this.$refNumSettings(this.appointmentWarehouse);
      this.appointmentTableRows.loadType.value = this.$loadTypeName(this.appointment.loadType);
      this.appointmentTableRows.refNumber.label = refNumSettings.displayName;
      this.appointmentTableRows.refNumber.isHidden = !refNumSettings.isVisible;
      this.appointmentTableRows.refNumber.value = this.appointment.refNumber;
      this.appointmentTableRows.dockName.value = this.appointment.dock.name;
    },
    async updateAppointment() {
      this.lockTagEditing = true;
      await axios
        .patch(`appointment/${this.appointment.id}`, {
          tags: this.tags
        })
        .then(() => {
          this.$store.dispatch('Appointments/trackMixpanelEvent', {
            appointment: this.appointment || {},
            change: 'Tags'
          });
        })
        .finally(() => (this.lockTagEditing = false));
    }
  },
  async mounted() {
    this.initializeRowValues();
    this.setCustomFieldsRows();
    this.loading = false;
    this.trackApptDetailsMixpanelEvent();
  },
  beforeDestroy() {
    this.$router.push({ query: {} });
  },
  watch: {
    tags() {
      if (JSON.stringify(this.tags) !== JSON.stringify(this.appointment.tags)) {
        this.updateAppointment();
      }
    },
    appointment(appointment) {
      this.initializeRowValues();
      this.setCustomFieldsRows();
      this.tags = appointment.tags;
    }
  }
};
</script>

<style lang="scss">
.appointment-notes > p:not(:only-child) {
  margin-bottom: 1px !important;
}
</style>
